import Layout from "@/components/Layout";
import { TablePaginator } from "@/components/TablePaginator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { useAtendimentos } from "@/hooks/useAtendimentos";
import { useIsMedico } from "@/hooks/useIsMedico";
import { usePermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ListarPaciente from "./components/novoAtendimento";
import { StatusAtendimento } from "./interfaces/atendimento.interface";

const Atendimentos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isNewAttendanceModalOpen, setIsNewAttendanceModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 5;

  // Verificação de permissões e cargo
  const { isAdmin } = usePermissions();
  const { isMedico } = useIsMedico();

  // Resetar para página 1 quando a busca mudar
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Recarregar atendimentos quando a página for montada (ao voltar de outra página)
  useEffect(() => {
    // Invalidar e refetch os atendimentos quando a página for montada
    queryClient.invalidateQueries({ queryKey: ['atendimentos'] });
  }, [location.pathname, queryClient]);

  // Usar hook para carregar atendimentos com filtro de pesquisa e paginação
  const { data, isLoading, error, refetch } = useAtendimentos({
    search: searchTerm || undefined,
    page,
    limit
  });

  const atendimentos = data?.data || [];
  const pagination = data?.pagination;

  const getStatusColor = (status: StatusAtendimento) => {
    switch (status) {
      case StatusAtendimento.CONCLUIDO:
        return "bg-success/10 text-success hover:bg-success/20";
      case StatusAtendimento.EM_ANDAMENTO:
        return "bg-warning/10 text-warning hover:bg-warning/20";
      case StatusAtendimento.AGENDADO:
        return "bg-blue-100 text-blue-800 hover:bg-blue-200";
      case StatusAtendimento.CANCELADO:
        return "bg-red-100 text-red-800 hover:bg-red-200";
      case StatusAtendimento.REAGENDADO:
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200";
      default:
        return "bg-muted";
    }
  };

  const getStatusLabel = (status: StatusAtendimento) => {
    switch (status) {
      case StatusAtendimento.CONCLUIDO:
        return "Concluído";
      case StatusAtendimento.EM_ANDAMENTO:
        return "Em Andamento";
      case StatusAtendimento.AGENDADO:
        return "Agendado";
      case StatusAtendimento.CANCELADO:
        return "Cancelado";
      case StatusAtendimento.REAGENDADO:
        return "Reagendado";
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleNewAttendance = () => {
    setIsNewAttendanceModalOpen(true);
  };

  const handleSelectPatientAndDoctor = async (patient: any, doctor: any, tipoAtendimento?: any) => {
    try {
      // Chamar API para criar/iniciar atendimento usando a função api
      const atendimento = await api.post('/api/atendimentos', {
        id_paciente: patient.id,
        id_medico: doctor.id,
        id_tipo_atendimento: tipoAtendimento?.id || undefined,
        motivo_atendimento: tipoAtendimento?.nome || 'Consulta médica'
      });

      toast({
        title: "Atendimento iniciado",
        description: `Atendimento iniciado para ${patient.name}`,
      });

      // Navegar para a página de edição de atendimento com os dados do paciente e médico
      navigate(`/atendimento/${atendimento.result.id}`, {
        state: {
          patient,
          doctor,
          atendimento: atendimento.result
        }
      });
    } catch (error) {
      console.error('Erro ao iniciar atendimento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar o atendimento. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">Atendimentos</h2>
            <p className="text-muted-foreground mt-1">Registro e histórico de atendimentos</p>
          </div>
          {(isAdmin || isMedico) && (
            <Button className="gap-2" onClick={handleNewAttendance}>
              <Plus className="h-4 w-4" />
              Novo Atendimento
            </Button>
          )}
        </div>

        {/* Input de Pesquisa */}
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Pesquisar por nome do paciente ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="rounded-md border">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Carregando atendimentos...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center p-8 text-red-500">
                <span>Erro ao carregar atendimentos. Tente novamente.</span>
              </div>
            ) : atendimentos.length === 0 ? (
              <div className="flex items-center justify-center p-8 text-muted-foreground">
                <span>Nenhum atendimento encontrado.</span>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Tipo de Atendimento</TableHead>
                    <TableHead>Médico</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {atendimentos.map((atendimento) => (
                    <TableRow key={atendimento.id}>
                      <TableCell className="font-medium">
                        {atendimento.paciente?.pessoa?.nome || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{formatDate(atendimento.data_atendimento)}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatTime(atendimento.data_atendimento)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {atendimento.tipoAtendimento?.nome || atendimento.motivo_atendimento || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {atendimento.medico?.pessoa?.nome || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getStatusColor(atendimento.status_atendimento)}>
                          {getStatusLabel(atendimento.status_atendimento)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/atendimento/${atendimento.id}`)}
                            title="Editar"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {pagination && atendimentos.length > 0 && (
            <TablePaginator
              pagination={pagination}
              onPageChange={setPage}
            />
          )}
        </Card>
      </div>

      {/* Modal de Seleção de Paciente e Médico */}
      <ListarPaciente
        isOpen={isNewAttendanceModalOpen}
        onClose={() => setIsNewAttendanceModalOpen(false)}
        onSelectPatient={handleSelectPatientAndDoctor}
      />
    </Layout>
  );
};

export default Atendimentos;
